import { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';
import { useState as useHookState } from '@hookstate/core';

import { IFieldConfiguration } from '@models/FieldConfiguration.model';
import { AssetContext } from '@pages/Asset/Asset.query';
import { getEquipmentValue, getPermissionData } from '@utils';

import { ClickAwayListener } from '@material-ui/core';

import { NavigationContext } from '@layout/navigation/navigation';
import { IEquipmentWithoutProduct } from '@models/Equipment.model';
import { isNonNullable } from '@utils/isNonNullable';
import { ITooltip } from '@components/Tooltip';
import { ChangeAssignee } from '../../../ChangeAssignee/ChangeAssignee';
import {
  Container,
  IconLeft,
  IconRight,
  Line,
  Separator,
  TextLeft,
  TextRight,
  TextRightEmpty,
  ToolTipDiv,
} from './PinnedField.styles';
import { PinnedFieldContent } from './PinnedFieldContent';
import { EditableField } from '../../../Data/components/EditableField/EditableField';
import { EditableCustomField } from '../../../Data/components/EditableCustomField/EditableCustomField';
import { MembersQueryResult } from './PinnedField.types';
import { EditableDateField } from '../../../Data/components/EditableDateField/EditableDateField';

interface IFields {
  item: IFieldConfiguration;
  removeItem: () => void;
  index: number;
  setOverflow: Dispatch<SetStateAction<boolean>>;
}
const EMPTY_TIP = 'Only internal data and assignee can be edited.';

export const PinnedField = ({
  item,
  removeItem,
  index,
  setOverflow,
}: IFields) => {
  const {
    equipmentQuery,
    unpinPinnedData,
    reOrderPinnedData,
    restorePinnedData,
    membersQuery,
    customFieldQuery,
    isEditingField,
  } = useContext(AssetContext);

  const { role } = useContext(NavigationContext);

  const [show, setShow] = useState(false);
  const open = useHookState(false);

  const handleChange = (isOverflowed: boolean) => {
    setShow(isOverflowed);
  };

  const equipment = equipmentQuery?.data;

  const customFields = customFieldQuery?.data;

  const isEditing = useMemo(() => {
    return (
      unpinPinnedData.get() ||
      reOrderPinnedData.get() ||
      restorePinnedData.get()
    );
  }, [unpinPinnedData.get(), reOrderPinnedData.get(), restorePinnedData.get()]);

  if (!equipment) {
    return <div />;
  }

  const value = getEquipmentValue(item, equipment);

  const assigneePermission = getPermissionData({
    action: 'assignee',
    owner: false,
    role,
    type: '',
  });

  const disabledConditions = () => {
    if (!show && assigneePermission.allowed) return true;
    if (!value) return true;
    if (isEditingField === item.fieldName) return true;
    if (isEditingField === item.customField?.id) return true;

    return false;
  };

  const handleClickAway = () => {
    if (item.fieldName === 'userName' && !isEditing) {
      open.set(false);
    }
  };

  const fieldsContent = equipment.fields;

  return (
    <>
      {index !== 0 && <Separator />}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Container data-testid="pinned-data-item">
          <Line
            pointer={
              item.fieldName === 'userName' &&
              !isEditing &&
              assigneePermission.allowed
            }
            data-tip
            data-for={`tooltip${item.id}`}
            onClick={() => {
              if (
                item.fieldName === 'userName' &&
                !isEditing &&
                assigneePermission.allowed
              ) {
                open.set(true);
                setOverflow(true);
              }
            }}
          >
            {reOrderPinnedData.get() && (
              <IconLeft>
                <BlueIcon icon="drag-handle-vertical" color="#C1C4D6" />
              </IconLeft>
            )}

            <TextLeft>{item.name}</TextLeft>
            {(() => {
              if (
                item.fieldName === 'productPage' &&
                value.indexOf('bhphotovideo') !== -1
              ) {
                return <TextRightEmpty>Empty</TextRightEmpty>;
              }
              const fieldJSX = getFieldJSX(
                item,
                equipment,
                handleChange,
                membersQuery,
                value,
              );
              if (isNonNullable(fieldJSX)) {
                return fieldJSX;
              }
              const customFieldsFiltered = customFields?.find(
                field => field.id === item.customField?.id,
              );
              if (item.customField && customFields && customFieldsFiltered) {
                const fieldContent = fieldsContent.find(
                  field => field.customFieldId === item.customField?.id,
                );
                return (
                  <TextRight
                    onChange={handleChange}
                    limitOfLines={1}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      lineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    <EditableCustomField
                      key={item.customField.id}
                      fieldContent={fieldContent}
                      customField={customFieldsFiltered}
                      equipment={equipment}
                      pinnedFields
                    />
                  </TextRight>
                );
              }

              return (
                <TextRightEmpty
                  data-tip={EMPTY_TIP}
                  data-for={`tooltip-empty-${item.id}`}
                >
                  Empty
                </TextRightEmpty>
              );
            })()}

            {unpinPinnedData.get() && (
              <IconRight onClick={removeItem}>
                <BlueIcon icon="delete" color="#D14343" />
              </IconRight>
            )}
          </Line>
          <ITooltip
            className="tooltip"
            id={`tooltip${item.id}`}
            disable={disabledConditions()}
            effect="solid"
            multiline
            scrollHide
            place="bottom"
          >
            <ToolTipDiv>
              {assigneePermission.allowed
                ? value
                : assigneePermission.message.replace('<br />', ' ')}
            </ToolTipDiv>
          </ITooltip>
          <ITooltip
            id={`tooltip-empty-${item.id}`}
            disable={item.fieldName === 'userName'}
          />
          {item.fieldName === 'userName' && (
            <ChangeAssignee open={open} setOverflow={setOverflow} />
          )}
        </Container>
      </ClickAwayListener>
    </>
  );
};
function getFieldJSX(
  item: IFieldConfiguration,
  equipment: IEquipmentWithoutProduct,
  handleChange: (isOverflowed: boolean) => void,
  membersQuery: MembersQueryResult | undefined,
  value: string,
) {
  const { fieldName } = item;

  if (!fieldName) return null;

  switch (fieldName) {
    case 'serial':
    case 'purchasedFrom':
    case 'purchasedPrice':
    case 'sla':
    case 'vendor':
    case 'installedFirmwareVersion':
    case 'installedSoftwareVersion': {
      let fieldValue: string;

      if (fieldName === 'purchasedPrice' || fieldName === 'vendor') {
        // Nb: Vendor is maybeUndefined, purchasedPrice is nullish.
        const preValue = equipment[fieldName];
        fieldValue = preValue ? preValue.toString() : '';
      } else {
        fieldValue = equipment[fieldName];
      }
      return (
        <TextRight
          onChange={handleChange}
          limitOfLines={1}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            lineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <EditableField
            pinnedFields
            parameter={fieldName}
            content={fieldValue}
          />
        </TextRight>
      );
    }
    case 'purchasedDate':
    case 'installDate':
    case 'licenseStartDate':
    case 'licenseEndDate': {
      let fieldValue = equipment[fieldName];
      fieldValue = equipment[fieldName];
      return (
        <TextRight
          onChange={handleChange}
          limitOfLines={1}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            lineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <EditableDateField
            pinnedData
            parameter={fieldName}
            content={fieldValue}
          />
        </TextRight>
      );
    }

    default: {
      if (!value) return null;
      return (
        <TextRight
          onChange={handleChange}
          limitOfLines={1}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            lineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <PinnedFieldContent {...{ item, membersQuery, equipment, value }} />
        </TextRight>
      );
    }
  }
}
